/**
 * §P.6 · Quién ha leído un aviso — **y quién no**.
 *
 * La segunda mitad es la útil, y por eso es la que va arriba. «Lo han leído 34 de
 * 48» no es accionable; «estas catorce familias no lo han visto» es a quién llamar
 * antes del sábado.
 *
 * ## Leído y confirmado no son lo mismo
 *
 * `read_at` es que la app lo enseñó; `acknowledged_at` es que una persona pulsó
 * «enterado». Solo el segundo prueba que alguien lo vio, y solo existe si el aviso
 * pedía confirmación. Mezclarlos sería dar por avisada a una familia porque su
 * móvil abrió una pantalla.
 *
 * ## Los alumnos al lado del nombre
 *
 * Quien lleva un club de 120 familias no reconoce «Carmen Ruiz»; reconoce «la madre
 * de Lucía y Marco». Por eso `students` viaja en cada acuse y se pinta.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fechaYHora } from '@/shared/ui/formato';
import type { ResumenDeAcuses } from '@/club/avisos/tipos';

export function AcusesDeAviso({
  club,
  avisoId,
  alCerrar,
}: {
  club: string;
  avisoId: string;
  alCerrar: () => void;
}) {
  const acuses = useQuery({
    queryKey: ['acuses', club, avisoId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .announcements({ announcementId: avisoId })
        .receipts.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as ResumenDeAcuses;
    },
  });

  if (acuses.isPending) {
    return <Cargando filas={6} etiqueta="Cargando quién lo ha leído" />;
  }

  if (acuses.isError || !acuses.data) {
    return (
      <PanelError
        problema={aProblema(acuses.error)}
        reintentar={() => void acuses.refetch()}
      />
    );
  }

  const r = acuses.data;
  const sinLeer = r.receipts.filter((a) => a.read_at === null);
  const leidos = r.receipts.filter((a) => a.read_at !== null);
  // `acknowledged` solo tiene sentido si el aviso pedía confirmación: si nadie
  // podía confirmarlo, un «0 confirmados» acusa a las familias de nada.
  const pedíaConfirmacion = r.receipts.some((a) => a.acknowledged_at !== null);

  return (
    <section>
      <div className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCerrar}>
          ← Avisos
        </button>
      </div>

      <h1>Quién lo ha leído</h1>

      <div className={sinLeer.length > 0 ? 'aviso' : 'aviso aviso-calma'}>
        <strong>
          {r.read} de {r.reach} lo han abierto
          {pedíaConfirmacion && `, y ${r.acknowledged} lo han confirmado`}.
        </strong>{' '}
        {sinLeer.length === 0
          ? 'No falta nadie.'
          : sinLeer.length === 1
            ? 'Falta una familia.'
            : `Faltan ${sinLeer.length} familias.`}
      </div>

      {/* Lo primero, y no al final: es la lista con la que se hace algo. */}
      {sinLeer.length > 0 && (
        <>
          <h2 className="seccion">Todavía no lo han visto</h2>
          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Familia</th>
                  <th>Por quién</th>
                  <th>Correo</th>
                  <th>Le llegó</th>
                </tr>
              </thead>
              <tbody>
                {sinLeer.map((a) => (
                  <tr key={a.user_id} className="fila-aviso">
                    <td>{a.name}</td>
                    {/* «La madre de Lucía y Marco», que es como se la reconoce. */}
                    <td className="tenue">
                      {a.students.length === 0 ? '—' : a.students.join(', ')}
                    </td>
                    <td className="tenue">{a.email ?? '—'}</td>
                    <td className="tenue">
                      {a.delivered_at ? (
                        fechaYHora(a.delivered_at)
                      ) : (
                        /*
                         * Ni siquiera le llegó: eso no es que no lo haya leído, es
                         * que no tiene la app o no tiene cuenta. Es un problema
                         * distinto y se arregla en otro sitio.
                         */
                        <strong>no le llegó</strong>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="tenue">
            A quien pone <strong>«no le llegó»</strong> no le falta leerlo: le
            falta la app. Se mira en su ficha, en los tutores.
          </p>
        </>
      )}

      {leidos.length > 0 && (
        <>
          <h2 className="seccion">Lo han leído</h2>
          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Familia</th>
                  <th>Por quién</th>
                  <th>Lo abrió</th>
                  {pedíaConfirmacion && <th>Lo confirmó</th>}
                </tr>
              </thead>
              <tbody>
                {leidos.map((a) => (
                  <tr key={a.user_id}>
                    <td>{a.name}</td>
                    <td className="tenue">
                      {a.students.length === 0 ? '—' : a.students.join(', ')}
                    </td>
                    <td className="tenue">{fechaYHora(a.read_at)}</td>
                    {pedíaConfirmacion && (
                      <td className="tenue">
                        {a.acknowledged_at ? (
                          fechaYHora(a.acknowledged_at)
                        ) : (
                          <span>solo lo abrió</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pedíaConfirmacion && (
            <p className="tenue">
              <strong>Abrirlo y confirmarlo no es lo mismo.</strong> Lo primero
              es que la app lo enseñó; lo segundo, que alguien pulsó «enterado».
              Solo lo segundo prueba que lo vio una persona.
            </p>
          )}
        </>
      )}
    </section>
  );
}
